import React from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { getDashboardUsers } from 'api/dashboards';
import { addUserToDashboard, removeUserFromDashboard } from 'api/users';
import { Dashboard } from 'models/dashboard';

import { Button, Form, Input } from '@flightaware/ui-components';
import Loader from 'components/Loader';
import './ManageDashboardUsers.scss';
import { userAllQuery } from 'routes/admin';

interface ManageDashboardUsersProps {
    dashboard: Dashboard;
}

interface ManageDashboardUsersFormData {
    userId: {
        label: string;
        value: string;
    };
}

interface DashboardUserRelation {
    dashboardId: string;
    userId: string;
}

const dashboardUserSchema = () =>
    yup.object().shape({
        userId: yup.mixed().required('Field is required.'),
    });

const getDashboardUsersQuery = (dashboardId: string) => ({
    queryKey: ['dashboards', dashboardId, 'users'],
    queryFn: async () => getDashboardUsers(dashboardId),
});

const useAddUserToDashboardMutation = (dashboardId: string) => {
    const queryClient = useQueryClient();

    return useMutation<boolean, Error, DashboardUserRelation>(
        (relation) => addUserToDashboard(relation.userId, relation.dashboardId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['dashboards', dashboardId, 'users']);
            },
        }
    );
};

const useRemoveUserFromDashboardMutation = (dashboardId: string) => {
    const queryClient = useQueryClient();

    return useMutation<boolean, Error, DashboardUserRelation>(
        (relation) => removeUserFromDashboard(relation.userId, relation.dashboardId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['dashboards', dashboardId, 'users']);
            },
        }
    );
};

export default function ManageDashboardUsers(props: ManageDashboardUsersProps) {
    const { data: dashboardUsers = [], isLoading: isLoadingDashboardUsers } = useQuery(
        getDashboardUsersQuery(props.dashboard.id)
    );
    const { data: allUsers = [], isLoading: isLoadingAllUsers } = useQuery(userAllQuery());

    const isLoading = isLoadingDashboardUsers || isLoadingAllUsers;

    const { mutate: addUserToDashboard } = useAddUserToDashboardMutation(props.dashboard.id);
    const { mutate: removeUserFromDashboard } = useRemoveUserFromDashboardMutation(props.dashboard.id);

    const formMethods = useForm({ mode: 'onBlur', resolver: yupResolver(dashboardUserSchema()) });
    const { reset } = formMethods;

    return (
        <div className="manage-dashboard-users" data-testid="manage-dashboard-users">
            <h2 className="manage-dashboard-users__heading">Users</h2>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {dashboardUsers.length === 0 ? (
                        <div className="manage-dashboard-users__no-users">Dashboard has no users attached</div>
                    ) : (
                        <ul className="manage-dashboard-users__list">
                            {dashboardUsers.map((dashboardUser) => (
                                <li className="manage-dashboard-users__user" key={dashboardUser.id}>
                                    {dashboardUser.name} ({dashboardUser.username}){' '}
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        handleClick={() => {
                                            removeUserFromDashboard(
                                                {
                                                    userId: dashboardUser.id,
                                                    dashboardId: props.dashboard.id,
                                                },
                                                {
                                                    onError: () => toast.error('Error removing user from dashboard'),
                                                    onSuccess: () => {
                                                        toast.success(
                                                            `Removed user ${dashboardUser.id} from dashboard ${props.dashboard.id}`
                                                        );
                                                    },
                                                }
                                            );
                                        }}
                                    >
                                        remove
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <Form
                        onSubmit={(data: ManageDashboardUsersFormData) => {
                            addUserToDashboard(
                                {
                                    userId: data.userId.value,
                                    dashboardId: props.dashboard.id,
                                },
                                {
                                    onError: () => toast.error('Error adding user to dashboard'),
                                    onSuccess: () => {
                                        reset();
                                        toast.success(
                                            `Added user ${data.userId.value} to dashboard ${props.dashboard.id}`
                                        );
                                    },
                                }
                            );
                        }}
                        formMethods={formMethods}
                        buttonPosition="left"
                        submitButton={{ show: true, text: 'Add user', size: 'large' }}
                        resetButton={{ show: false }}
                        customClass="manage-dashboard-users__form"
                    >
                        <Input
                            type="select"
                            name="userId"
                            label="Add user to dashboard"
                            placeholder="Select a user..."
                            isClearable={false}
                            // The options only reflect FSDB users that aren't already added to the given dashboard
                            options={allUsers
                                .filter((user) => !dashboardUsers.some((dashboardUser) => dashboardUser.id === user.id))
                                .map((user) => ({
                                    label: `${user.name} (${user.username})`,
                                    value: user.id,
                                }))}
                        />
                    </Form>
                </>
            )}
        </div>
    );
}

ManageDashboardUsers.propTypes = {
    dashboard: PropTypes.object,
};
